import React, { useState } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { Eye, EyeOff, Lock, Mail, User, AlertCircle, ArrowRight, Loader2, CheckCircle2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';
import { useAuthStore } from '@/stores/useAuthStore';
import { apiClient } from '@/api/client';
import { toast } from 'sonner';
import { NuvemshopLoginButton } from './NuvemshopLoginButton';

export const RegisterForm: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { login } = useAuthStore();

  const searchParams = new URLSearchParams(location.search);
  const queryEmail = searchParams.get('email') || '';
  const returnUrl = searchParams.get('returnUrl') || searchParams.get('redirect');

  const [fullName, setFullName] = useState('');
  const [email, setEmail] = useState(queryEmail);
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);

  const getPasswordStrength = (): { level: string; score: number; color: string } => {
    if (!password) return { level: 'Nenhum', score: 0, color: 'bg-muted' };
    let score = 0;
    if (password.length >= 6) score += 1;
    if (password.length >= 8) score += 1;
    if (/[A-Z]/.test(password) && /[a-z]/.test(password)) score += 1;
    if (/[0-9]/.test(password) || /[^A-Za-z0-9]/.test(password)) score += 1;

    if (score <= 1) return { level: 'Fraca', score: 25, color: 'bg-destructive' };
    if (score === 2) return { level: 'Razoável', score: 50, color: 'bg-amber-500' };
    if (score === 3) return { level: 'Boa', score: 75, color: 'bg-sky-500' };
    return { level: 'Forte', score: 100, color: 'bg-emerald-500' };
  };

  const validate = (): boolean => {
    if (!fullName.trim()) {
      setErrorMessage('Por favor, insira seu nome completo.');
      return false;
    }
    if (!email.trim()) {
      setErrorMessage('Por favor, insira seu endereço de e-mail.');
      return false;
    }
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(email.trim())) {
      setErrorMessage('Por favor, insira um endereço de e-mail válido.');
      return false;
    }
    if (password.length < 6) {
      setErrorMessage('A senha deve ter pelo menos 6 caracteres.');
      return false;
    }
    if (password !== confirmPassword) {
      setErrorMessage('As senhas não coincidem.');
      return false;
    }
    return true;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setErrorMessage(null);

    if (!validate()) return;

    setIsLoading(true);

    try {
      const response = await apiClient.post<{
        token?: string;
        user?: { id?: string; email: string; fullName?: string };
        userId?: string;
        email?: string;
        fullName?: string;
        message?: string;
      }>('/api/Auth/register', {
        email: email.trim(),
        password,
        fullName: fullName.trim(),
      });

      const data = response.data;
      toast.success('Conta criada com sucesso!');

      if (data.token) {
        const userProfile = data.user || {
          id: data.userId || 'usr_' + Date.now(),
          email: data.email || email.trim(),
          fullName: data.fullName || fullName.trim(),
        };
        login(data.token, userProfile);
        if (typeof localStorage !== 'undefined') {
          localStorage.setItem('token', data.token);
          localStorage.setItem('user', JSON.stringify(userProfile));
        }

        const from = returnUrl || '/conversations';
        navigate(from, { replace: true });
      } else {
        const redirectSuffix = returnUrl ? `&returnUrl=${encodeURIComponent(returnUrl)}` : '';
        navigate(`/login?registered=true${redirectSuffix}`, { replace: true });
      }
    } catch (err: any) {
      const serverMessage =
        err?.response?.data?.message ||
        err?.response?.data?.title ||
        'Falha no cadastro. Por favor, verifique seus detalhes e tente novamente.';
      setErrorMessage(serverMessage);
      toast.error(serverMessage);
    } finally {
      setIsLoading(false);
    }
  };

  const strength = getPasswordStrength();

  return (
    <div className="w-full space-y-6">
      {/* Header */}
      <div className="space-y-2 text-left">
        <h1 className="text-2xl sm:text-3xl font-bold tracking-tight text-foreground">
          Crie sua conta de lojista
        </h1>
        <p className="text-sm text-muted-foreground">
          Conecte suas lojas e automatize o suporte ao cliente em minutos
        </p>
      </div>

      {/* Error alert */}
      {errorMessage && (
        <Alert variant="destructive">
          <AlertCircle className="h-4 w-4" />
          <AlertTitle>Erro de Cadastro</AlertTitle>
          <AlertDescription className="text-xs">
            {errorMessage}
          </AlertDescription>
        </Alert>
      )}

      {/* Form */}
      <div className="space-y-4">
        <NuvemshopLoginButton label="Continuar com Nuvemshop" />

        <div className="relative">
          <div className="absolute inset-0 flex items-center">
            <span className="w-full border-t border-muted-foreground/20" />
          </div>
          <div className="relative flex justify-center text-xs uppercase">
            <span className="bg-background px-2 text-muted-foreground">
              Ou registre-se com e-mail
            </span>
          </div>
        </div>

        <form onSubmit={handleSubmit} className="space-y-3.5">
          {/* Full Name */}
        <div className="space-y-1">
          <Label htmlFor="fullName" className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">
            Nome Completo
          </Label>
          <div className="relative">
            <User className="absolute left-3 top-2.5 h-4 w-4 text-muted-foreground pointer-events-none" />
            <Input
              id="fullName"
              type="text"
              placeholder="Maria Silva"
              autoComplete="name"
              className="pl-9"
              value={fullName}
              onChange={(e) => {
                setFullName(e.target.value);
                if (errorMessage) setErrorMessage(null);
              }}
              disabled={isLoading}
              required
            />
          </div>
        </div>

        {/* Email Address */}
        <div className="space-y-1">
          <Label htmlFor="register-email" className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">
            Endereço de E-mail
          </Label>
          <div className="relative">
            <Mail className="absolute left-3 top-2.5 h-4 w-4 text-muted-foreground pointer-events-none" />
            <Input
              id="register-email"
              type="email"
              placeholder="nome@loja.com"
              autoComplete="email"
              className="pl-9"
              value={email}
              onChange={(e) => {
                setEmail(e.target.value);
                if (errorMessage) setErrorMessage(null);
              }}
              disabled={isLoading}
              required
            />
          </div>
        </div>

        {/* Password */}
        <div className="space-y-1">
          <Label htmlFor="register-password" className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">
            Senha
          </Label>
          <div className="relative">
            <Lock className="absolute left-3 top-2.5 h-4 w-4 text-muted-foreground pointer-events-none" />
            <Input
              id="register-password"
              type={showPassword ? 'text' : 'password'}
              placeholder="Mínimo de 6 caracteres"
              autoComplete="new-password"
              className="pl-9 pr-9"
              value={password}
              onChange={(e) => {
                setPassword(e.target.value);
                if (errorMessage) setErrorMessage(null);
              }}
              disabled={isLoading}
              required
            />
            <button
              type="button"
              className="absolute right-3 top-2.5 text-muted-foreground hover:text-foreground focus:outline-none"
              onClick={() => setShowPassword(!showPassword)}
              tabIndex={-1}
              aria-label={showPassword ? 'Ocultar senha' : 'Mostrar senha'}
            >
              {showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
            </button>
          </div>

          {/* Password strength bar */}
          {password && (
            <div className="pt-1.5 space-y-1">
              <div className="h-1 w-full bg-secondary rounded-full overflow-hidden">
                <div
                  className={`h-full ${strength.color} transition-all duration-300`}
                  style={{ width: `${strength.score}%` }}
                />
              </div>
              <div className="flex justify-between items-center text-[10px] text-muted-foreground">
                <span>Força: <span className="font-medium text-foreground">{strength.level}</span></span>
                <span>{password.length >= 6 ? '✓ Tamanho OK' : 'Mín. 6 carac.'}</span>
              </div>
            </div>
          )}
        </div>

        {/* Confirm Password */}
        <div className="space-y-1">
          <Label htmlFor="confirm-password" className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">
            Confirmar Senha
          </Label>
          <div className="relative">
            <Lock className="absolute left-3 top-2.5 h-4 w-4 text-muted-foreground pointer-events-none" />
            <Input
              id="confirm-password"
              type={showPassword ? 'text' : 'password'}
              placeholder="Digite sua senha novamente"
              autoComplete="new-password"
              className="pl-9"
              value={confirmPassword}
              onChange={(e) => {
                setConfirmPassword(e.target.value);
                if (errorMessage) setErrorMessage(null);
              }}
              disabled={isLoading}
              required
            />
          </div>
          {confirmPassword && password === confirmPassword && (
            <div className="flex items-center gap-1 text-[11px] text-emerald-600 dark:text-emerald-400 pt-0.5">
              <CheckCircle2 className="h-3 w-3" />
              <span>As senhas coincidem</span>
            </div>
          )}
        </div>

        {/* Submit Button */}
        <Button
          type="submit"
          className="w-full font-semibold shadow-md gap-2 mt-2"
          disabled={isLoading}
        >
          {isLoading ? (
            <>
              <Loader2 className="h-4 w-4 animate-spin" />
              <span>Criando Conta...</span>
            </>
          ) : (
            <>
              <span>Criar Conta</span>
              <ArrowRight className="h-4 w-4" />
            </>
          )}
        </Button>
        </form>
      </div>

      {/* Switch to Login link */}
      <div className="pt-2 text-center text-sm text-muted-foreground">
        Já possui uma conta?{' '}
        <Link
          to={`/login${location.search}`}
          className="font-medium text-foreground hover:text-primary underline underline-offset-4 transition-colors"
        >
          Entrar
        </Link>
      </div>
    </div>
  );
};
